/** Slugify a material name into a filename-safe prefix, e.g. "Film and Flexible Packaging" -> "film-and-flexible-packaging". */
export function sanitizeMaterialSlug(name: string): string {
  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '');
  return slug || 'material';
}

/** Strip path separators/control characters from a user-supplied filename before it's ever written
 * anywhere (prefixed, uploaded, shown). Keeps the extension, collapses whitespace, caps length. */
export function sanitizeFileName(originalName: string): string {
  const lastDot = originalName.lastIndexOf('.');
  const base = lastDot > 0 ? originalName.slice(0, lastDot) : originalName;
  const ext = lastDot > 0 ? originalName.slice(lastDot) : '';
  const cleanBase = base
    // eslint-disable-next-line no-control-regex -- intentional: strips control chars from untrusted filenames
    .replace(/[/\\?%*:|"<>\u0000-\u001F]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
  const cleanExt = ext.replace(/[^a-zA-Z0-9.]/g, '').slice(0, 10);
  return `${cleanBase || 'file'}${cleanExt}`;
}

/** Prefix an uploaded file's name with its material so operators can tell them apart at a glance. */
export function buildSpecFileName(materialName: string, originalFileName: string): string {
  return `${sanitizeMaterialSlug(materialName)}--${sanitizeFileName(originalFileName)}`;
}

export const ALLOWED_UPLOAD_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.heic'];
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export type FileValidation = { ok: true } | { ok: false; reason: string };

/** Client-side gate before a file ever enters state — the <input accept> attribute is only a picker
 * hint and can be bypassed ("All Files"), so real type + size checks happen here. */
export function validateUploadFile(file: File): FileValidation {
  const lower = file.name.toLowerCase();
  const hasAllowedExt = ALLOWED_UPLOAD_EXTENSIONS.some((ext) => lower.endsWith(ext));
  if (!hasAllowedExt) {
    return { ok: false, reason: `"${file.name}" wasn't added — only PDF, JPG, PNG, WEBP or HEIC files are allowed.` };
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return { ok: false, reason: `"${file.name}" wasn't added — files must be 10MB or smaller.` };
  }
  return { ok: true };
}
